import { deviceAliases } from '../core/state';
import { formatClock } from '../core/format';
import { t } from '../ui/lang';
import { makeDeviceCard } from './device-card';
import { isReordering } from '../farms/device-order';
// ── Card size / cards-per-row controls ──────────────────────────────────────────
//
// Stored per browser (not per farm): the operator picks one comfortable card size and
// it follows them across every grid, the dashboard included.
const CARD_SCALE_KEY = 'deviceCardScale';
const CARDS_PER_ROW_KEY = 'deviceCardsPerRow';
export const CARDS_PER_ROW_MAX = 10;
export function loadCardScale() {
    const s = parseFloat(localStorage.getItem(CARD_SCALE_KEY) ?? '');
    return Number.isFinite(s) && s >= 0.3 && s <= 1.3 ? s : 1;
}
function saveCardScale(s) {
    localStorage.setItem(CARD_SCALE_KEY, String(s));
}
/** Percent step of the card-size slider. Divides the 30–130 range evenly, so every
 *  reachable value stays a whole percent. */
const CARD_SCALE_STEP_PCT = 2;
export function loadCardsPerRow() {
    const n = parseInt(localStorage.getItem(CARDS_PER_ROW_KEY) ?? '', 10);
    return Number.isInteger(n) && n >= 1 && n <= CARDS_PER_ROW_MAX ? n : 6;
}
function saveCardsPerRow(n) {
    localStorage.setItem(CARDS_PER_ROW_KEY, String(n));
}
/**
 * Apply the size settings as CSS custom properties. They inherit, so the dashboard
 * sets them once on the container holding every per-farm section instead of on each
 * grid individually.
 */
export function applyCardSizeVars(el, scale = loadCardScale(), perRow = loadCardsPerRow()) {
    el.style.setProperty('--card-scale', String(scale));
    el.style.setProperty('--cards-per-row', String(perRow));
}
/** The two range sliders for the page header. Ids are fixed — one grid surface at a time. */
export function cardSizeControlsHtml() {
    const pct = Math.round(loadCardScale() * 100);
    const perRow = loadCardsPerRow();
    return `
        <label class="card-size-control">
            <span>${t('devices.card_size')}</span>
            <input type="range" id="card-size-slider" min="30" max="130" step="${CARD_SCALE_STEP_PCT}" value="${pct}" />
            <span id="card-size-label">${pct}%</span>
        </label>
        <label class="card-size-control">
            <span>${t('devices.cards_per_row')}</span>
            <input type="range" id="cards-per-row-slider" min="1" max="${CARDS_PER_ROW_MAX}" step="1" value="${perRow}" />
            <span id="cards-per-row-label">${perRow}</span>
        </label>
    `;
}
/**
 * Wire the sliders built by {@link cardSizeControlsHtml} to `target` (the element
 * carrying the CSS vars). `onChange` runs after each adjustment — grids use it to
 * re-evaluate their scroll arrows.
 */
export function wireCardSizeControls(target, onChange) {
    const slider = document.getElementById('card-size-slider');
    const sizeLabel = document.getElementById('card-size-label');
    slider?.addEventListener('input', () => {
        const s = parseInt(slider.value, 10) / 100;
        saveCardScale(s);
        if (sizeLabel)
            sizeLabel.textContent = `${slider.value}%`;
        target.style.setProperty('--card-scale', String(s));
        onChange?.();
    });
    const rowSlider = document.getElementById('cards-per-row-slider');
    const rowLabel = document.getElementById('cards-per-row-label');
    rowSlider?.addEventListener('input', () => {
        const n = parseInt(rowSlider.value, 10);
        saveCardsPerRow(n);
        if (rowLabel)
            rowLabel.textContent = String(n);
        target.style.setProperty('--cards-per-row', String(n));
        onChange?.();
    });
}
/**
 * Build a `.device-grid-wrap` (relative) containing the left/right floating scroll
 * arrows and the `.device-grid` itself, with all scroll wiring attached. Detached —
 * the caller appends it. `id` names the grid element (the farm page keeps
 * `device-grid` so existing selectors still resolve).
 */
export function createDeviceGrid(opts = {}) {
    const wrap = document.createElement('div');
    wrap.className = 'device-grid-wrap';
    wrap.innerHTML = `
        <button class="device-grid-arrow device-grid-arrow--left" type="button" aria-label="${t('devices.scroll_left')}" hidden>&lsaquo;</button>
        <div class="device-grid"></div>
        <button class="device-grid-arrow device-grid-arrow--right" type="button" aria-label="${t('devices.scroll_right')}" hidden>&rsaquo;</button>
    `;
    const arrowLeft = wrap.querySelector('.device-grid-arrow--left');
    const arrowRight = wrap.querySelector('.device-grid-arrow--right');
    const grid = wrap.querySelector('.device-grid');
    if (opts.id)
        grid.id = opts.id;
    // Each arrow is shown only when there's content to scroll toward in its direction.
    const updateArrows = () => {
        const overflows = grid.scrollWidth > grid.clientWidth + 1;
        arrowLeft.hidden = !overflows || grid.scrollLeft <= 1;
        arrowRight.hidden = !overflows || grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
    };
    const scrollByCard = (dir) => {
        // Advance by one card width (clientWidth / cards-per-row) where that's a sizeable
        // step, else ~80% of the visible width so a single click always moves usefully.
        const perRow = Math.max(1, parseInt(getComputedStyle(grid).getPropertyValue('--cards-per-row'), 10) || 1);
        const cardStep = grid.clientWidth / perRow;
        const step = Math.max(cardStep, grid.clientWidth * 0.8);
        grid.scrollBy({ left: dir * step, behavior: 'smooth' });
    };
    arrowLeft.addEventListener('click', () => scrollByCard(-1));
    arrowRight.addEventListener('click', () => scrollByCard(1));
    grid.addEventListener('scroll', updateArrows);
    const onResize = () => {
        // Self-clean: once this render's grid is gone from the DOM, drop the listener.
        if (!document.body.contains(grid)) {
            window.removeEventListener('resize', onResize);
            return;
        }
        updateArrows();
    };
    window.addEventListener('resize', onResize);
    return { wrap, grid, updateArrows };
}
let lastGridLayoutKey = null;
let lastGridClocks = new Map();
let lastGridClicker = new Map();
// Per-udid stream holder ("taken by <login>", undefined when free), so a poll that only
// changed who holds a device — including a Force disconnect that frees it — re-renders
// just that card in place (the "In use by …" placeholder ⇄ idle/connect card).
let lastGridTakenBy = new Map();
// Per-udid workspace claim list + the running clicker's owner, same treatment: gaining or
// losing a claim only flips the card's own pin button, and a change of clicker owner only
// its "In work:" line.
let lastGridClaims = new Map();
function claimKey(d) {
    // clickerStartedBy rides along because a restart by ANOTHER operator can change the
    // "In work:" line without clickerRunning ever flipping between two polls. A close-shift
    // run starting or ending repaints the same card (its subtitle and pulsing shift icon).
    const shift = d.shiftRunning ? `shift:${d.shiftStartedBy ?? ''}` : '';
    return `${(d.claimedBy ?? []).join(',')}|${d.clickerStartedBy ?? ''}|${shift}`;
}
/**
 * Stable, order-sensitive key of the fields that decide which cards exist and how their
 * icon row renders. Deliberately excludes clickerRunning, takenByLogin AND claimedBy: a
 * clicker start/stop, a stream take/release or a workspace pin only changes the involved
 * card's own controls, so we patch that one card in place rather than rebuilding the whole
 * grid — a full rebuild destroys sibling/pending cards, making them flicker out and back.
 *
 * `scopeKey` distinguishes the surface the DOM was built for (a farm name, or the
 * dashboard) so switching surfaces can never look like "nothing changed".
 */
export function deviceLayoutKey(scopeKey, devs) {
    const parts = devs.map((d) => {
        const displayName = deviceAliases.get(d.udid) || d['ro.product.model'] || d.udid;
        return `${d.udid}|${d.state}|${d.farmName ?? ''}|${displayName}`;
    });
    return `${scopeKey}\n${parts.join('\n')}`;
}
/** Force the next poll to do a full rebuild. Called from every non-poll render path. */
export function invalidateDeviceGridCache() {
    lastGridLayoutKey = null;
    lastGridClocks = new Map();
    lastGridClicker = new Map();
    lastGridTakenBy = new Map();
    lastGridClaims = new Map();
}
/** Record the snapshot the current DOM reflects, so the next poll can diff against it. */
export function seedDeviceGridCache(scopeKey, devs) {
    lastGridLayoutKey = deviceLayoutKey(scopeKey, devs);
    lastGridClocks = new Map(devs.map((d) => [d.udid, { updatedAt: d.updatedAt }]));
    lastGridClicker = new Map(devs.map((d) => [d.udid, !!d.clickerRunning]));
    lastGridTakenBy = new Map(devs.map((d) => [d.udid, d.takenByLogin]));
    lastGridClaims = new Map(devs.map((d) => [d.udid, claimKey(d)]));
}
/**
 * Clock-only fast path: patch each card's "last update" indicator in place without
 * rebuilding the card, so the time stays fresh but the icon row never reflows.
 */
function patchDeviceClocks(root, devs) {
    for (const d of devs) {
        if (!d.lastSeen)
            continue;
        const card = root.querySelector(`.device-card[data-udid="${CSS.escape(d.udid)}"]`);
        const timeEl = card?.querySelector('.device-updated__time');
        if (timeEl)
            timeEl.textContent = formatClock(d.lastSeen);
        const wrap = card?.querySelector('.device-updated');
        if (wrap) {
            wrap.setAttribute('title', t('devices.last_online', { time: new Date(d.lastSeen).toLocaleString() }));
            wrap.style.removeProperty('display');
        }
    }
}
/**
 * Per-card fast path: re-render only the changed card(s) in place, so a clicker
 * start/stop or a stream take/release doesn't rebuild the whole grid (which destroyed
 * sibling/pending cards and made them flicker out and back). makeDeviceCard re-attaches
 * any live inline stream from the inlineStreams map, so replacing the node preserves an
 * in-progress stream.
 */
export function patchDeviceCards(root, changed, cardOptsFor) {
    for (const d of changed) {
        const card = root.querySelector(`.device-card[data-udid="${CSS.escape(d.udid)}"]`);
        if (card)
            card.replaceWith(makeDeviceCard(d, cardOptsFor(d)));
    }
}
/**
 * Poll entry point. Returns 'rebuild' when the set/order/naming of cards actually moved
 * and the caller must re-render the surface (then call {@link seedDeviceGridCache});
 * returns 'patched' when the difference was absorbed into the existing DOM.
 */
export function diffDeviceGrid(scopeKey, devs, root, cardOptsFor) {
    const layoutKey = deviceLayoutKey(scopeKey, devs);
    if (lastGridLayoutKey === null || layoutKey !== lastGridLayoutKey)
        return 'rebuild';
    // Same set of cards — patch in place instead of rebuilding.
    if (devs.some((d) => lastGridClocks.get(d.udid)?.updatedAt !== d.updatedAt)) {
        patchDeviceClocks(root, devs);
        lastGridClocks = new Map(devs.map((d) => [d.udid, { updatedAt: d.updatedAt }]));
    }
    // A clicker start/stop, a stream take/release (incl. a Force disconnect that clears
    // takenByLogin) or a workspace claim change only flips the involved card's controls.
    // Union the diffs so a card that changed on several fronts is patched once.
    const cardChanged = new Map();
    for (const d of devs) {
        if ((lastGridClicker.get(d.udid) ?? false) !== !!d.clickerRunning)
            cardChanged.set(d.udid, d);
        if (lastGridTakenBy.get(d.udid) !== d.takenByLogin)
            cardChanged.set(d.udid, d);
        if ((lastGridClaims.get(d.udid) ?? '') !== claimKey(d))
            cardChanged.set(d.udid, d);
    }
    if (cardChanged.size) {
        patchDeviceCards(root, [...cardChanged.values()], cardOptsFor);
        for (const d of cardChanged.values()) {
            lastGridClicker.set(d.udid, !!d.clickerRunning);
            lastGridTakenBy.set(d.udid, d.takenByLogin);
            lastGridClaims.set(d.udid, claimKey(d));
        }
    }
    return 'patched';
}
/**
 * Reconcile `grid` to `items`, in that order.
 *
 * A preserved card keeps its DOM node and acts as an ANCHOR pinning the slot for the
 * cards after it; every other card is rebuilt and inserted at its position. (Appending
 * rebuilt cards at the end instead floats a freshly-connected card to the top of the
 * grid on the next pass.) Cards whose device left `items` are dropped unless `keep`
 * vouches for them — that is the caller's absence grace for a phone briefly missing
 * from one provider poll.
 *
 * No-op while a drag-reorder is live: re-slotting now would move nodes out from under
 * the pointer and snap the dragged card back. The caller's next pass repaints.
 */
export function reconcileGrid(grid, items, keep) {
    if (isReordering())
        return;
    grid.querySelector('.empty')?.remove();
    const seen = new Set();
    let anchor = null; // last card placed in order; null ⇒ grid front
    for (const it of items) {
        seen.add(it.udid);
        const existing = grid.querySelector(`:scope > [data-udid="${CSS.escape(it.udid)}"]`);
        if (existing && it.preserve) {
            anchor = existing;
            continue;
        }
        existing?.remove();
        const card = it.make();
        grid.insertBefore(card, anchor ? anchor.nextSibling : grid.firstChild);
        anchor = card;
    }
    grid.querySelectorAll(':scope > [data-udid]').forEach((el) => {
        const u = el.dataset['udid'];
        if (u && !seen.has(u) && !keep?.(u))
            el.remove();
    });
}
/**
 * Release live embedded screens whose card is no longer anywhere in the document. Cloud
 * connections are tracked in a module map, not on the card, so a card removed by
 * reconciliation (or by a whole surface being re-rendered away) would otherwise leave
 * the entry behind for ever.
 */
export function dropOrphanedScreens(connected, disconnect) {
    for (const udid of [...connected]) {
        if (!document.querySelector(`.device-card[data-udid="${CSS.escape(udid)}"]`))
            disconnect(udid);
    }
}
